import { useState } from "react";

import { useTranslation } from "react-i18next";

import { ReturnCondition } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import { bandFor, bandRangeText, conditionLabel, DEDUCTION_BANDS, deductionPctFor, refundAfterDeduction } from "../../utils/returnsRules";
import ActionPanelFrame, { ReturnActionPanelProps } from "./ActionPanelFrame";

interface RefundPanelProps extends ReturnActionPanelProps {
  kind: "refund_full" | "refund_deduction";
  /** From "Refund now" after a check: the band and a percentage inside it. Defaults to the check's band, at its top. */
  initialCondition?: ReturnCondition;
  initialPct?: number;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const clampPct = (n: number) => Math.max(0, Math.min(100, n));

/**
 * Refund once the item is back: in full, or with a deduction inside eBay's condition bands. The
 * Received and checked band is pinned beside the matching radio; customer service picks the
 * percentage within it and the amount follows, or types the amount and the percentage follows.
 * Whichever box was typed in last is the source of truth, so a typed £ amount is sent as typed.
 */
export default function RefundPanel({ kind, detail, initialCondition, initialPct, onConfirm, onCancel, busy }: RefundPanelProps) {
  const { t } = useTranslation("returns");
  const estimate = detail.estimatedRefund;
  // eBay sometimes gives only a total; a deduction needs the item part on its own.
  const itemised = estimate?.item !== null && estimate?.item !== undefined;
  const item = estimate?.item ?? 0;
  const postage = estimate?.postage ?? 0;
  const total = estimate?.total ?? item + postage;
  const inspected = detail.inspection?.condition ?? null;
  const startCondition = initialCondition ?? inspected ?? "Good";
  const startBand = bandFor(startCondition);
  const startPct = initialPct !== undefined ? Math.min(Math.max(initialPct, startBand.min), startBand.max) : startBand.max;
  const [condition, setCondition] = useState<ReturnCondition>(startCondition);
  const [source, setSource] = useState<"pct" | "amount">("pct");
  const [pctText, setPctText] = useState(String(startPct));
  const [amountText, setAmountText] = useState(() => refundAfterDeduction(item, postage, startPct).toFixed(2));

  const band = bandFor(condition);
  const typedAmount = Number(amountText);
  const amount = source === "amount" ? (Number.isFinite(typedAmount) && amountText.trim() !== "" ? round2(typedAmount) : NaN) : refundAfterDeduction(item, postage, clampPct(Number(pctText) || 0));
  const pct = source === "amount" ? (Number.isFinite(amount) ? deductionPctFor(item, postage, amount) : NaN) : clampPct(Number(pctText) || 0);
  const inBand = Number.isFinite(pct) && pct >= band.min && pct <= band.max;

  // A band change pins the percentage inside it and the amount follows.
  const chooseBand = (c: ReturnCondition) => {
    const next = bandFor(c);
    const nextPct = Math.min(Math.max(Number.isFinite(pct) ? pct : next.max, next.min), next.max);
    setCondition(c);
    setSource("pct");
    setPctText(String(nextPct));
    setAmountText(refundAfterDeduction(item, postage, nextPct).toFixed(2));
  };

  const changePct = (text: string) => {
    setSource("pct");
    setPctText(text);
    setAmountText(refundAfterDeduction(item, postage, clampPct(Number(text) || 0)).toFixed(2));
  };

  const changeAmount = (text: string) => {
    setSource("amount");
    setAmountText(text);
    const value = Number(text);
    if (text.trim() !== "" && Number.isFinite(value)) setPctText(String(deductionPctFor(item, postage, value)));
  };

  if (kind === "refund_full" || !itemised) {
    return (
      <ActionPanelFrame
        kind="refund_full"
        detail={detail}
        confirmLabel={t("Confirm refund {{amount}} – send to eBay", { amount: PriceFormat(total) })}
        onConfirm={(message) => onConfirm({ message, amount: total })}
        onCancel={onCancel}
        busy={busy}
      >
        <dl className="grid grid-cols-[150px_minmax(0,1fr)] gap-x-2.5 gap-y-1.5 rounded-lg border border-gray-200 px-3 py-2.5 text-[12px]">
          <dt className="text-gray-500">{t("Refund to buyer")}</dt>
          <dd className="font-semibold text-gray-900">{PriceFormat(total)}</dd>
          {estimate?.item !== null && estimate?.item !== undefined && estimate.postage !== null && (
            <>
              <dt className="text-gray-500">{t("Made up of")}</dt>
              <dd className="text-gray-700">{t("{{item}} item + {{postage}} original postage", { item: PriceFormat(estimate.item), postage: PriceFormat(estimate.postage) })}</dd>
            </>
          )}
        </dl>
        {kind === "refund_deduction" && <span className="text-[11px] text-gray-500">{t("eBay hasn't itemised this refund, so a deduction can't be worked out — full refund only.")}</span>}
      </ActionPanelFrame>
    );
  }

  return (
    <ActionPanelFrame
      kind="refund_deduction"
      detail={detail}
      title={`${t("Refund with a deduction")} · ${t("eBay's condition bands")}`}
      canConfirm={inBand && Number.isFinite(amount) && amount >= 0}
      confirmLabel={t("Confirm refund {{amount}} – send to eBay", { amount: PriceFormat(Number.isFinite(amount) ? amount : 0) })}
      templateExtras={{ refund_amount: PriceFormat(Number.isFinite(amount) ? amount : 0) }}
      onConfirm={(message) => onConfirm({ message, amount, deductionPct: round2(pct), condition })}
      onCancel={onCancel}
      busy={busy}
    >
      <div role="radiogroup" aria-label={t("Condition band")} className="flex flex-col gap-1 text-[12px]">
        {DEDUCTION_BANDS.map((b) => {
          const active = b.condition === condition;
          return (
            <label key={b.condition} className={classNames("flex items-center gap-2 rounded-md border px-2 py-1.5", active ? "border-primary-600 bg-primary-50" : "border-gray-200")}>
              <input type="radio" name="band" checked={active} onChange={() => chooseBand(b.condition)} disabled={busy} className="border-gray-300 text-primary-600 focus:ring-primary-500" />
              <span className="flex-1">
                <span className={active ? "font-semibold text-gray-900" : "text-gray-800"}>{conditionLabel(b.condition)}</span>
                {inspected === b.condition && (
                  <span className="ml-1 rounded bg-green-100 px-1.5 text-[11px] font-semibold text-green-700">{t("Checked as {{condition}}", { condition: conditionLabel(b.condition) })}</span>
                )}
              </span>
              <span className={active ? "text-gray-700" : "text-gray-500"}>{bandRangeText(b)}</span>
            </label>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-2 text-[12px]">
        <label className="flex flex-col gap-0.5 text-gray-700">
          {t("Deduction")}
          <span className="flex items-center overflow-hidden rounded-lg border border-gray-300">
            <input type="number" min={band.min} max={band.max} step="any" value={pctText} onChange={(e) => changePct(e.target.value)} aria-label={t("Deduction percent")} disabled={busy} className="w-full border-0 px-2.5 py-1.5 text-[13px] focus:ring-0" />
            <span className="flex items-center self-stretch border-l border-gray-200 px-2.5 text-gray-500">%</span>
          </span>
        </label>
        <label className="flex flex-col gap-0.5 text-gray-700">
          {t("Refund to buyer")}
          <span className="flex items-center overflow-hidden rounded-lg border border-gray-300">
            <span className="flex items-center self-stretch border-r border-gray-200 px-2.5 text-gray-500">£</span>
            <input type="number" min="0" step="0.01" value={amountText} onChange={(e) => changeAmount(e.target.value)} aria-label={t("Refund amount")} disabled={busy} className="w-full border-0 px-2.5 py-1.5 text-[13px] font-semibold focus:ring-0" />
          </span>
        </label>
      </div>
      <span className={classNames("text-[11px]", inBand ? "text-gray-500" : "text-red-700")}>
        {inBand
          ? t("{{item}} item − {{pct}}% = {{refund}}. Original postage {{postage}} is always refunded. Deductions recover lost value only — never postage or restocking. Not available once eBay has stepped in.", {
              item: PriceFormat(item),
              pct,
              refund: PriceFormat(amount),
              postage: PriceFormat(postage),
            })
          : t("{{pct}}% is outside eBay's {{condition}} band ({{range}}). Pick a percentage inside it or change the band.", { pct: Number.isFinite(pct) ? pct : "—", condition: conditionLabel(condition), range: bandRangeText(band) })}
      </span>
    </ActionPanelFrame>
  );
}
